<script setup lang="ts" generic="T extends Record<string, any> = Record<string, any>">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, useAttrs, useSlots, watch } from 'vue'
import { AlignJustify, Check, RefreshCw, RotateCcw, Settings2 } from '@lucide/vue'
import {
  RrTable,
  RrPagination,
  useDragReorder,
  type TableColumnType,
  type TableRowSelection,
  type TableDensity,
  type TableFilters,
  type TableSorter,
  type TablePaginationConfig,
  type TableChangeExtra
} from '@xjlv2/ui'
import AdminEmptyState from '../feedback/AdminEmptyState.vue'
import AdminResourceState from '../feedback/AdminResourceState.vue'
import AdminSkeleton from '../feedback/AdminSkeleton.vue'

defineOptions({ inheritAttrs: false })
const attrs = useAttrs()
const slots = useSlots()
const tableAttrs = computed(() => Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style')))
const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    count?: string | number
    minWidth?: number | string
    empty?: boolean
    dataSource?: T[]
    columns?: TableColumnType<T>[]
    rowKey?: string | ((record: T) => string | number)
    rowSelection?: TableRowSelection<T>
    emptyTitle?: string
    emptyDescription?: string
    loading?: boolean
    error?: string
    errorTitle?: string
    stickyActions?: boolean
    tableClass?: string
    draggable?: boolean
    dragDisabled?: boolean
    density?: TableDensity
    size?: 'small' | 'middle' | 'large'
    showTools?: boolean
    showRefresh?: boolean
    showDensity?: boolean
    showColumnSetting?: boolean
    selectedCount?: number
    showClearSelection?: boolean
    current?: number
    pageSize?: number
    total?: number
    pagination?: boolean | TablePaginationConfig
    pageSizeOptions?: number[]
    hideOnSinglePage?: boolean
    paginationAlign?: 'left' | 'center' | 'right' | 'between'
  }>(),
  {
    title: '',
    description: '',
    count: '',
    minWidth: 720,
    empty: false,
    dataSource: undefined,
    columns: undefined,
    rowKey: 'id',
    rowSelection: undefined,
    emptyTitle: '暂无数据',
    emptyDescription: '',
    loading: false,
    error: '',
    errorTitle: '数据加载失败',
    stickyActions: false,
    tableClass: '',
    draggable: false,
    dragDisabled: false,
    density: undefined,
    size: undefined,
    showTools: false,
    showRefresh: true,
    showDensity: true,
    showColumnSetting: true,
    selectedCount: 0,
    showClearSelection: true,
    current: undefined,
    pageSize: undefined,
    total: undefined,
    pagination: true,
    pageSizeOptions: () => [10, 20, 50, 100],
    hideOnSinglePage: false,
    paginationAlign: 'right'
  }
)

const emit = defineEmits<{
  'update:current': [current: number]
  'update:pageSize': [size: number]
  'update:density': [density: TableDensity]
  'update:columns': [columns: TableColumnType<T>[]]
  'change': [pagination: TablePaginationConfig, filters: TableFilters, sorter: TableSorter<T>, extra: TableChangeExtra<T>]
  'refresh': []
  'retry': []
  'clear-selection': []
  'reorder': [ids: string[]]
  'column-toggle': [key: string, visible: boolean]
  'page-change': [page: number]
  'page-size-change': [pageSize: number]
  'row-click': [record: T, index: number, event: MouseEvent]
}>()

defineSlots<{
  default?: (props: Record<string, any>) => any
  head?: (props: Record<string, any>) => any
  body?: (props: Record<string, any>) => any
  title?: (props: Record<string, any>) => any
  extra?: (props: Record<string, any>) => any
  actions?: (props: { row: T; record: T; column?: any; text?: any; index?: number }) => any
  'actions-left'?: (props: Record<string, any>) => any
  'actions-right'?: (props: Record<string, any>) => any
  toolbar?: (props: Record<string, any>) => any
  empty?: (props: Record<string, any>) => any
  footer?: (props: Record<string, any>) => any
  [key: string]: ((props: any) => any) | undefined
}>()

// Density state (controlled or uncontrolled)
const localDensity = ref<TableDensity>(props.density ?? 'default')
const currentDensity = computed(() => localDensity.value)

watch(
  () => props.density,
  (val) => {
    if (val) localDensity.value = val
  }
)

function setDensity(val: TableDensity) {
  localDensity.value = val
  emit('update:density', val)
  densityMenuOpen.value = false
}

// Tool dropdowns
const densityMenuOpen = ref(false)
const columnMenuOpen = ref(false)
const initialColumns = shallowRef<TableColumnType<T>[] | null>(null)
const internalColumns = shallowRef<TableColumnType<T>[]>(props.columns ? [...props.columns] : [])

watch(
  () => props.columns,
  (columns) => {
    internalColumns.value = columns ? [...columns] : []
    if (columns?.length) {
      if (!initialColumns.value) {
        initialColumns.value = columns.map((col) => ({ ...col }))
      }
    }
  },
  { immediate: true, deep: true }
)

function toggleDensityMenu() {
  densityMenuOpen.value = !densityMenuOpen.value
  if (densityMenuOpen.value) columnMenuOpen.value = false
}

function toggleColumnMenu() {
  columnMenuOpen.value = !columnMenuOpen.value
  if (columnMenuOpen.value) densityMenuOpen.value = false
}

function closeDropdowns(event?: MouseEvent) {
  const target = event?.target as HTMLElement | null
  if (target?.closest('.rr-data-table__tool-menu') || target?.closest('.rr-data-table__tool-btn')) {
    return
  }
  densityMenuOpen.value = false
  columnMenuOpen.value = false
}

onMounted(() => {
  if (typeof document !== 'undefined') {
    document.addEventListener('click', closeDropdowns)
  }
})

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('click', closeDropdowns)
  }
})

function getColKey(col: TableColumnType<T>, idx: number): string {
  return String(col.key ?? col.dataIndex ?? idx)
}

function getColLabel(col: TableColumnType<T>): string {
  return typeof col.title === 'string' ? col.title : String(col.key ?? col.dataIndex ?? '')
}

// Column settings
function toggleColumn(key: string) {
  const next = internalColumns.value.map((col, idx) => {
    const k = getColKey(col, idx)
    if (k === key && !col.disabled) {
      const nextVis = col.visible === false ? true : false
      emit('column-toggle', key, nextVis)
      return { ...col, visible: nextVis }
    }
    return col
  })
  internalColumns.value = next
  emit('update:columns', next)
}

function resetColumns() {
  if (!initialColumns.value) return
  const next = initialColumns.value.map((col) => ({ ...col }))
  internalColumns.value = next
  emit('update:columns', next)
}

// Drag & Drop
const bodyRef = ref<HTMLElement | null>(null)
const drag = useDragReorder(bodyRef, {
  handle: '.drag-handle',
  dataIdAttr: 'data-id',
  disabled: () => !props.draggable || props.dragDisabled,
  onReorder: (ids) => emit('reorder', ids)
})

watch(
  [() => props.draggable, () => props.dragDisabled, () => props.loading],
  async () => {
    await nextTick()
    drag.sync()
  },
  { immediate: true }
)
onBeforeUnmount(drag.destroy)

const hasTools = computed(
  () => props.showTools || props.showRefresh || props.showDensity || props.showColumnSetting
)

const tableRecords = computed<T[]>(() => props.dataSource ?? [])
const hasRecordSource = computed(() => props.dataSource !== undefined)
const isDataMode = computed(() => Boolean(props.columns?.length && !slots.body && !slots.head))

const isTableEmpty = computed(() => {
  if (props.empty) return true
  if (!props.loading && !props.error && hasRecordSource.value && tableRecords.value.length === 0) {
    return true
  }
  return false
})

const paginationConfig = computed<TablePaginationConfig>(() => typeof props.pagination === 'object' ? props.pagination : {})
const innerCurrent = ref(paginationConfig.value.defaultCurrent ?? 1)
const innerPageSize = ref(paginationConfig.value.defaultPageSize ?? 10)
const activePage = computed(() => paginationConfig.value.current ?? props.current ?? innerCurrent.value)
const activePageSize = computed(() => paginationConfig.value.pageSize ?? props.pageSize ?? innerPageSize.value)
const activeTotal = computed(() => paginationConfig.value.total ?? props.total)
const tablePagination = computed<false | TablePaginationConfig>(() => props.pagination === false ? false : {
  pageSizeOptions: props.pageSizeOptions,
  hideOnSinglePage: props.hideOnSinglePage,
  align: props.paginationAlign,
  ...paginationConfig.value,
  current: activePage.value,
  pageSize: activePageSize.value,
  ...(activeTotal.value === undefined ? {} : { total: activeTotal.value })
})
const currentSorter = shallowRef<TableSorter<T>>({})
const currentFilters = shallowRef<TableFilters>({})
const paginationProps = computed(() => {
  const { onChange: _change, onShowSizeChange: _size, ...options } = paginationConfig.value
  return options
})
function handleTableChange(pagination: TablePaginationConfig, filters: TableFilters, sorter: TableSorter<T>, extra: TableChangeExtra<T>) {
  currentSorter.value = sorter
  currentFilters.value = filters
  if (pagination.current !== undefined && pagination.pageSize !== undefined) updatePagination(pagination.current, pagination.pageSize, extra.action === 'paginate')
  emit('change', pagination, filters, sorter, extra)
}
const actualSelectedCount = computed(() => {
  if (props.selectedCount > 0) return props.selectedCount
  if (props.rowSelection?.selectedRowKeys) return props.rowSelection.selectedRowKeys.length
  return 0
})

function updatePagination(page: number, size: number, notify: boolean) {
  const pageChanged = page !== activePage.value
  const sizeChanged = size !== activePageSize.value
  innerCurrent.value = page
  innerPageSize.value = size
  if (pageChanged) emit('update:current', page)
  if (sizeChanged) emit('update:pageSize', size)
  if (notify) {
    if (sizeChanged) emit('page-size-change', size)
    else if (pageChanged) emit('page-change', page)
  }
}

function handlePaginationChange(page: number, size: number) {
  const sizeChanged = size !== activePageSize.value
  updatePagination(page, size, true)
  if (sizeChanged) paginationConfig.value.onShowSizeChange?.(page, size)
  paginationConfig.value.onChange?.(page, size)
  emit('change', { ...paginationConfig.value, current: page, pageSize: size, total: activeTotal.value }, currentFilters.value, currentSorter.value, { action: 'paginate', currentDataSource: tableRecords.value })
}
</script>

<template>
  <article
    class="rr-data-table"
    :style="attrs.style as any"
    :class="[
      attrs.class,
      size ? undefined : `is-density-${currentDensity}`,
      { 'has-sticky-actions': stickyActions }
    ]"
  >
    <!-- Table Header / Toolbar -->
    <header
      v-if="title || description || count !== '' || $slots['toolbar-left'] || $slots.toolbar || $slots['actions-left'] || $slots['toolbar-right'] || $slots['actions-right'] || hasTools || $slots.tools"
      class="rr-data-table__header"
    >
      <div class="rr-data-table__header-left">
        <div v-if="title || description || count !== ''" class="rr-data-table__heading">
          <div class="rr-data-table__title-row">
            <h3 v-if="title">{{ title }}</h3>
            <span v-if="count !== ''">{{ count }}</span>
          </div>
          <p v-if="description">{{ description }}</p>
        </div>
        <div v-if="$slots['toolbar-left'] || $slots.toolbar || $slots['actions-left']" class="rr-data-table__toolbar">
          <slot name="toolbar-left"><slot name="actions-left"><slot name="toolbar" /></slot></slot>
        </div>
      </div>

      <div class="rr-data-table__actions">
        <div v-if="$slots['toolbar-right'] || $slots['actions-right']" class="rr-data-table__toolbar-right">
          <slot name="toolbar-right"><slot name="actions-right" /></slot>
        </div>

        <div v-if="hasTools || $slots.tools" class="rr-data-table__tools">
          <slot name="tools" />

          <!-- Refresh Tool -->
          <button
            v-if="showRefresh || showTools"
            type="button"
            class="rr-data-table__tool-btn"
            :disabled="loading"
            title="刷新数据"
            aria-label="刷新数据"
            @click="emit('refresh')"
          >
            <RefreshCw :size="15" :class="{ 'is-spinning': loading }" aria-hidden="true" />
          </button>

          <!-- Density Tool -->
          <div v-if="showDensity || showTools" class="rr-data-table__tool-wrapper">
            <button
              type="button"
              class="rr-data-table__tool-btn"
              :class="{ 'is-active': densityMenuOpen }"
              title="表格密度"
              aria-label="表格密度"
              :aria-expanded="densityMenuOpen"
              @click.stop="toggleDensityMenu"
            >
              <AlignJustify :size="15" aria-hidden="true" />
            </button>
            <div v-if="densityMenuOpen" class="rr-data-table__tool-menu" role="menu" @click.stop>
              <button
                type="button"
                class="rr-data-table__menu-item"
                :class="{ 'is-selected': currentDensity === 'compact' }"
                role="menuitem"
                @click="setDensity('compact')"
              >
                <span>紧凑</span>
                <Check v-if="currentDensity === 'compact'" :size="14" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="rr-data-table__menu-item"
                :class="{ 'is-selected': currentDensity === 'middle' }"
                role="menuitem"
                @click="setDensity('middle')"
              >
                <span>中等</span>
                <Check v-if="currentDensity === 'middle'" :size="14" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="rr-data-table__menu-item"
                :class="{ 'is-selected': currentDensity === 'default' }"
                role="menuitem"
                @click="setDensity('default')"
              >
                <span>默认</span>
                <Check v-if="currentDensity === 'default'" :size="14" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="rr-data-table__menu-item"
                :class="{ 'is-selected': currentDensity === 'loose' }"
                role="menuitem"
                @click="setDensity('loose')"
              >
                <span>宽松</span>
                <Check v-if="currentDensity === 'loose'" :size="14" aria-hidden="true" />
              </button>
            </div>
          </div>

          <!-- Column Setting Tool -->
          <div
            v-if="(showColumnSetting || showTools) && internalColumns && internalColumns.length"
            class="rr-data-table__tool-wrapper"
          >
            <button
              type="button"
              class="rr-data-table__tool-btn"
              :class="{ 'is-active': columnMenuOpen }"
              title="列显隐设置"
              aria-label="列显隐设置"
              :aria-expanded="columnMenuOpen"
              @click.stop="toggleColumnMenu"
            >
              <Settings2 :size="15" aria-hidden="true" />
            </button>
            <div v-if="columnMenuOpen" class="rr-data-table__tool-menu rr-data-table__column-menu" role="menu" @click.stop>
              <div class="rr-data-table__column-menu-head">
                <strong>显示列</strong>
                <button type="button" class="ghost rr-data-table__reset-cols-btn" @click="resetColumns">
                  <RotateCcw :size="12" aria-hidden="true" />
                  <span>重置</span>
                </button>
              </div>
              <ul class="rr-data-table__column-list">
                <li v-for="(col, idx) in internalColumns" :key="getColKey(col, idx)">
                  <label class="rr-data-table__column-label">
                    <input
                      type="checkbox"
                      :checked="col.visible !== false"
                      :disabled="col.disabled"
                      @change="toggleColumn(getColKey(col, idx))"
                    />
                    <span>{{ getColLabel(col) }}</span>
                  </label>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Batch Action Banner -->
    <section
      v-if="actualSelectedCount > 0 || $slots.batchActions"
      class="rr-data-table__batch-bar"
      role="region"
      aria-label="批量操作"
    >
      <div class="rr-data-table__batch-info">
        <span>已选择 <strong>{{ actualSelectedCount }}</strong> 项</span>
        <button
          v-if="showClearSelection"
          type="button"
          class="ghost rr-data-table__clear-btn"
          @click="emit('clear-selection')"
        >
          清空
        </button>
      </div>
      <div v-if="$slots.batchActions" class="rr-data-table__batch-actions">
        <slot name="batchActions" />
      </div>
    </section>

    <!-- Error State -->
    <div v-if="!loading && error" class="rr-data-table__error-wrapper">
      <AdminResourceState
        :error="error"
        :title="errorTitle"
        :error-title="errorTitle"
        @retry="emit('retry')"
      >
        <template v-if="$slots.errorActions" #action>
          <slot name="errorActions" />
        </template>
      </AdminResourceState>
    </div>

    <!-- Mode A: Columns Data-Driven Table (Ant Design Recommended Mode) -->
    <template v-else-if="isDataMode">
      <RrTable
        v-bind="tableAttrs"
        :columns="internalColumns"
        :data-source="tableRecords"
        :row-key="rowKey"
        :row-selection="rowSelection"
        :loading="loading"
        :size="size ?? (currentDensity === 'compact' ? 'small' : currentDensity === 'middle' ? 'middle' : 'large')"
        :pagination="tablePagination"
        :empty-text="emptyTitle"
        :empty-description="emptyDescription"
        :min-width="minWidth"
        @row-click="(record, index, event) => emit('row-click', record, index, event)"
        @change="handleTableChange"
      >
        <template v-if="$slots.headerCell" #headerCell="slotProps">
          <slot name="headerCell" v-bind="slotProps" />
        </template>
        <template #bodyCell="slotProps">
          <slot v-if="$slots[`cell-${String(slotProps.column?.key || slotProps.column?.dataIndex || '')}`]" :name="`cell-${String(slotProps.column?.key || slotProps.column?.dataIndex || '')}`" v-bind="slotProps" :row="slotProps.record" />
          <slot v-else-if="(slotProps.column?.key === 'actions' || slotProps.column?.dataIndex === 'actions') && $slots.actions" name="actions" v-bind="slotProps" :row="slotProps.record" />
          <slot v-else-if="$slots.bodyCell" name="bodyCell" v-bind="slotProps" />
        </template>
        <template v-if="$slots.emptyActions" #emptyText>
          <AdminEmptyState :title="emptyTitle" :description="emptyDescription">
            <template #actions>
              <slot name="emptyActions" />
            </template>
          </AdminEmptyState>
        </template>
        <template v-if="$slots.summary" #summary>
          <slot name="summary" />
        </template>
      </RrTable>
    </template>

    <!-- Mode B: Loading Skeleton for Slot Table -->
    <AdminSkeleton v-else-if="loading && tableRecords.length === 0" variant="table" :rows="5" />

    <!-- Slot tables receive already paginated business rows. -->
    <div v-else class="rr-data-table__viewport">
      <table :class="tableClass" :style="{ minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth }">
        <thead><slot name="head" /></thead>
        <tbody ref="bodyRef">
          <tr v-if="isTableEmpty" class="rr-data-table__empty-row">
            <td :colspan="100" class="rr-data-table__empty-cell">
              <AdminEmptyState
                :title="emptyTitle"
                :description="emptyDescription"
              >
                <template v-if="$slots.emptyActions" #actions>
                  <slot name="emptyActions" />
                </template>
              </AdminEmptyState>
            </td>
          </tr>
          <slot v-else name="body" />
        </tbody>
      </table>
    </div>

    <!-- Table Footer / Embedded Pagination -->
    <footer v-if="$slots.footer || (!isDataMode && pagination && activeTotal !== undefined)" class="rr-data-table__footer">
      <slot name="footer">
        <RrPagination
          v-if="!isDataMode && pagination && activeTotal !== undefined"
          v-bind="paginationProps"
          :current="activePage"
          :page-size="activePageSize"
          :total="activeTotal"
          :page-size-options="pageSizeOptions"
          :hide-on-single-page="hideOnSinglePage"
          :align="paginationAlign"
          :loading="loading"
          @change="handlePaginationChange"
        />
      </slot>
    </footer>
  </article>
</template>

<style scoped>
.rr-data-table {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-panel);
}

.rr-data-table__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 52px;
  border-bottom: 1px solid var(--rr-color-border);
  padding: 10px 16px;
}

.rr-data-table__heading {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.rr-data-table__title-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.rr-data-table__title-row h3,
.rr-data-table__heading p {
  margin: 0;
}

.rr-data-table__title-row h3 {
  color: var(--rr-color-text-strong);
  font-size: 14px;
  font-weight: 700;
}

.rr-data-table__title-row span {
  border-radius: var(--rr-radius-tag);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  padding: 1px 6px;
}

.rr-data-table__heading p {
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.rr-data-table__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
}

.rr-data-table__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rr-data-table__tools {
  display: flex;
  align-items: center;
  gap: 6px;
}

.rr-data-table__toolbar + .rr-data-table__tools {
  border-left: 1px solid var(--rr-color-border);
  padding-left: 10px;
  margin-left: 2px;
}

.rr-data-table__tool-wrapper {
  position: relative;
}

.rr-data-table__tool-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px !important;
  min-height: 32px !important;
  max-width: 32px !important;
  max-height: 32px !important;
  padding: 0 !important;
  margin: 0 !important;
  box-sizing: border-box;
  border: 1px solid transparent !important;
  border-radius: var(--rr-radius-control);
  background: transparent !important;
  color: var(--rr-color-text-muted) !important;
  font-size: 14px;
  line-height: 1;
  font-weight: normal !important;
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease, color 150ms ease;
}

.rr-data-table__tool-btn:hover:not(:disabled) {
  border-color: var(--rr-color-border) !important;
  background: var(--rr-color-surface-subtle) !important;
  color: var(--rr-color-text-strong) !important;
}

.rr-data-table__tool-btn.is-active {
  border-color: var(--rr-color-primary-border) !important;
  background: var(--rr-color-primary-soft) !important;
  color: var(--rr-color-primary) !important;
}

.rr-data-table__tool-btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.is-spinning {
  animation: rr-spin 800ms linear infinite;
}

@keyframes rr-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .is-spinning {
    animation: none;
  }
}

.rr-data-table__tool-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 10;
  min-width: 120px;
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-panel);
  padding: 4px;
}

.rr-data-table__menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  border: 0;
  border-radius: var(--rr-radius-control);
  background: transparent;
  padding: 6px 10px;
  color: var(--rr-color-text);
  font-size: 12px;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}

.rr-data-table__menu-item:hover {
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-primary);
}

.rr-data-table__menu-item.is-selected {
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
  font-weight: 600;
}

.rr-data-table__column-menu {
  /* 列名菜单需要给中文列名和紧凑复选框留出稳定空间。 */
  width: 220px;
  max-height: 280px;
  overflow: auto;
  padding: 8px;
}

.rr-data-table__column-menu-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--rr-color-border);
  padding-bottom: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  color: var(--rr-color-text-strong);
}

.rr-data-table__reset-cols-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 0;
  background: transparent;
  color: var(--rr-color-primary);
  font-size: 11px;
  cursor: pointer;
  padding: 2px 4px;
}

.rr-data-table__column-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.rr-data-table__column-label {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 4px 6px;
  border-radius: var(--rr-radius-control);
  color: var(--rr-color-text);
  font-size: 12px;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  word-break: keep-all;
}

.rr-data-table__column-label input[type='checkbox'] {
  flex: 0 0 16px;
  width: 16px;
  min-width: 16px;
  height: 16px;
  min-height: 16px;
  margin: 0;
}

.rr-data-table__column-label:hover {
  background: var(--rr-color-surface-subtle);
}

.rr-data-table__batch-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--rr-color-primary-border);
  background: var(--rr-color-primary-soft);
  padding: 8px 14px;
  color: var(--rr-color-text-strong);
  font-size: 12px;
}

.rr-data-table__batch-info {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.rr-data-table__batch-info strong {
  color: var(--rr-color-primary);
}

.rr-data-table__clear-btn {
  border: 0;
  background: transparent;
  color: var(--rr-color-primary);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

.rr-data-table__batch-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rr-data-table__error-wrapper {
  padding: var(--rr-space-4);
}

.rr-data-table__viewport {
  min-width: 0;
  overflow: auto;
}

.rr-data-table table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  color: var(--rr-color-text);
  font-size: var(--rr-font-size-md);
}

:deep(th),
:deep(td) {
  box-sizing: border-box;
  border-bottom: 1px solid var(--rr-color-border);
  padding: var(--rr-space-4);
  text-align: left;
  vertical-align: middle;
  transition: padding 150ms ease, height 150ms ease;
}

:deep(th) {
  position: sticky;
  z-index: 2;
  top: 0;
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: var(--rr-font-size-md);
  font-weight: var(--rr-font-weight-bold);
  white-space: nowrap;
}

/* Density: Compact */
.is-density-compact :deep(th) {
  padding: var(--rr-space-2);
}
.is-density-compact :deep(td) {
  padding: var(--rr-space-2);
}

/* Density: Middle */
.is-density-middle :deep(th) {
  padding: var(--rr-space-3) var(--rr-space-2);
}
.is-density-middle :deep(td) {
  padding: var(--rr-space-3) var(--rr-space-2);
}

/* Density: Loose */
.is-density-loose :deep(th) {
  padding: var(--rr-space-5) var(--rr-space-4);
}
.is-density-loose :deep(td) {
  padding: var(--rr-space-5) var(--rr-space-4);
}

:deep(tbody tr:last-child td) {
  border-bottom: 0;
}

:deep(tbody tr) {
  transition: background-color 120ms ease;
}

:deep(tbody tr:hover) {
  background: var(--rr-color-surface-subtle);
}

.has-sticky-actions :deep(th:last-child),
.has-sticky-actions :deep(td:last-child) {
  position: sticky;
  z-index: 1;
  right: 0;
  background: inherit;
  box-shadow: -10px 0 14px -14px rgb(15 23 42 / 42%);
}

.has-sticky-actions :deep(th:last-child) {
  z-index: 3;
  background: var(--rr-color-surface-subtle);
}

.rr-data-table__footer {
  border-top: 1px solid var(--rr-color-border);
  padding: 10px 16px;
  background: var(--rr-color-surface);
}

.rr-data-table__empty-row:hover > td,
.rr-data-table__empty-cell {
  background: transparent !important;
  text-align: center;
  padding: 32px 16px;
  border-bottom: 0 !important;
}
</style>
